import {
  DatePicker,
  Input,
  Modal,
  Space,
  Typography,
  Select,
  Upload,
  notification,
  Alert,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { Form, Formik } from "formik";
import moment from "moment";
import React, { useRef, useState } from "react";
import * as Yup from "yup";

import { createProject, updateProjectById } from "../../store/api/projects";
import { PROJECT_STATUS_OPTIONS, PROJECT_STATUSES } from "../../constants";

import "react-quill/dist/quill.snow.css";

const { Title, Text } = Typography;

const AddProjectPopup = ({
  closePopup,
  item,
  onCreateSuccess,
  onUpdateSuccess,
}) => {
  const formikRef = useRef(null);

  const handleCancel = () => {
    closePopup();
  };

  const [confirmLoading, setConfirmLoading] = useState(false);

  const initialValues = {
    title: item?.title ?? "",
    status: item?.status ?? PROJECT_STATUSES.ONGOING,
    projectDate: item?.projectDate ? moment(item?.projectDate) : null,
    tags: item?.tags ?? "",
    file: item?.filePath ?? null,
  };

  const addValidationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    status: Yup.string().required("Status is required"),
    projectDate: Yup.string().required("Project date is required"),
    file: Yup.mixed().required("Thumbnail is required"),
    tags: Yup.string().nullable().optional(),
  });

  const editValidationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    status: Yup.string().required("Status is required"),
    projectDate: Yup.string().required("Project date is required"),
    tags: Yup.string().nullable().optional(),
  });

  const addNotice = (values) => {
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("status", values.status);
    formData.append("projectDate", values.projectDate);
    formData.append("tags", values.tags);
    formData.append("file", values.file);

    setConfirmLoading(true);

    createProject(formData)
      .then(({ data }) => {
        setConfirmLoading(false);
        notification.success({ message: "Successfully created project" });
        !!onCreateSuccess && onCreateSuccess(data);
        closePopup();
      })
      .catch((error) => {
        setConfirmLoading(false);
        notification.error({ message: error.response.data.message });
      });
  };

  const updateNotice = (values) => {
    const data = {
      id: item.id,
      title: values.title,
      status: values.status,
      projectDate: values.projectDate,
      tags: values.tags,
    };

    setConfirmLoading(true);

    updateProjectById(item.id, data)
      .then(({ data }) => {
        setConfirmLoading(false);
        notification.success({ message: data.message });
        !!onUpdateSuccess && onUpdateSuccess();
        closePopup();
      })
      .catch((error) => {
        setConfirmLoading(false);
        notification.error({ message: error.response.data.message });
      });
  };

  const onSubmit = (values) => {
    if (item) {
      updateNotice(values);
    } else {
      addNotice(values);
    }
  };

  return (
    <>
      <Modal
        title={item ? "Edit Project" : "Add Project"}
        open={true}
        confirmLoading={confirmLoading}
        onOk={() => {
          formikRef.current.submitForm();
        }}
        width={768}
        onCancel={handleCancel}
      >
        <>
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={item ? editValidationSchema : addValidationSchema}
            onSubmit={onSubmit}
          >
            {({ errors, touched, handleChange, values, setFieldValue }) => {
              return (
                <Form>
                  <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size={20}
                  >
                    <div>
                      <Title level={5}>Title *</Title>
                      <Input
                        placeholder="Enter project title"
                        name="title"
                        value={values.title}
                        onChange={handleChange}
                      />
                      {errors.title && touched.title ? (
                        <Text type="danger">{errors.title}</Text>
                      ) : null}
                    </div>

                    <div>
                      <Title level={5}>Status *</Title>
                      <Select
                        style={{ width: "100%" }}
                        placeholder="Select status"
                        name="status"
                        value={values.status}
                        onChange={(value) => setFieldValue("status", value)}
                      >
                        {PROJECT_STATUS_OPTIONS.map(({ label, value }) => (
                          <Select.Option value={value} key={value}>
                            {label}
                          </Select.Option>
                        ))}
                      </Select>
                      {errors.status && touched.status ? (
                        <Text type="danger">{errors.status}</Text>
                      ) : null}
                    </div>

                    <div>
                      <Title level={5}>Project Date *</Title>
                      <DatePicker
                        style={{ width: "100%" }}
                        placeholder="Enter project date"
                        name="projectDate"
                        value={values.projectDate}
                        onChange={(date) => setFieldValue("projectDate", date)}
                        format="MM/DD/YYYY"
                      />
                      {errors.projectDate && touched.projectDate ? (
                        <Text type="danger">{errors.projectDate}</Text>
                      ) : null}
                    </div>

                    <div>
                      <Title level={5}>Studies / Tasks</Title>
                      <Input
                        placeholder="e.g. Detailed Engineering Study # Feasibility study"
                        name="tags"
                        value={values.tags}
                        onChange={handleChange}
                      />
                      <p
                        style={{
                          fontSize: "0.75",
                          color: "#777777",
                          marginTop: "4px",
                        }}
                      >
                        Please enter studies separated by '#'
                      </p>
                      {errors.tags && touched.tags ? (
                        <Text type="danger">{errors.tags}</Text>
                      ) : null}
                    </div>

                    {!item && (
                      <div>
                        <Title level={5}>Upload thumbnail</Title>
                        <Alert
                          message="Recommended file size: max 1MB."
                          type="warning"
                          style={{ marginTop: "4px", marginBottom: "16px" }}
                        ></Alert>
                        <Upload.Dragger
                          name="file"
                          action={"/"}
                          multiple={false}
                          fileList={values.file ? [values.file] : []}
                          onChange={(value) =>
                            setFieldValue("file", value.file.originFileObj)
                          }
                          accept=".jpeg,.jpg,.png"
                          listType="picture"
                        >
                          <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                          </p>
                          <p className="ant-upload-text">
                            Click or drag file to this area to upload
                          </p>
                          <p className="ant-upload-hint">
                            Support for a .jpeg, .jpg, .png
                          </p>
                        </Upload.Dragger>
                        {errors.file && touched.file ? (
                          <Text type="danger">{errors.file}</Text>
                        ) : null}
                      </div>
                    )}
                  </Space>
                </Form>
              );
            }}
          </Formik>
        </>
      </Modal>
    </>
  );
};

export default AddProjectPopup;
